"use client";

import { FrontDoorNotice } from "@/app/(front-door)/_components/FrontDoorNotice";
import {
  FRONT_DOOR_CARD_CLASS,
  FRONT_DOOR_FIELD_STYLES,
  FRONT_DOOR_PRIMARY_BUTTON_CLASS,
} from "@/app/(front-door)/_components/styles";
import { StateDropdown } from "@/app/_components/forms/StateDropdown";
import { isMarbleServicedState } from "@/app/_components/states/constants";
import { Button } from "@/app/_components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/app/_components/ui/form";
import { Input } from "@/app/_components/ui/input";
import { PhoneInput } from "@/app/_components/ui/phone-input";
import { RoleDropdown } from "@/app/events/[eventId]/register/_components/RoleDropdown";
import { SchoolSelect } from "@/app/events/[eventId]/register/_components/SchoolSelect";
import {
  trackWorkshopRegistrationFailed,
  trackWorkshopRegistrationFormStarted,
  trackWorkshopRegistrationSubmitted,
  trackWorkshopValidationErrorShown,
} from "@/lib/analytics/workshops";
import {
  RegistrationFormSchema,
  type RegistrationFormValues,
} from "@/lib/modules/events/workshops/schemas";
import type { WorkshopRegistrationPrefill } from "@/lib/modules/events/workshops/types";
import { workshopCalendarPath } from "@/lib/modules/events/workshops/utils";
import { z } from "zod";
import { useTRPC } from "@/trpc/client";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Check } from "lucide-react";
import { useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";

const FORM_ID = "workshop-register";

const OptionalMark = () => (
  <span className="font-normal text-muted-foreground">(optional)</span>
);

export function RegistrationForm({
  eventId,
  token,
  language,
  isProfessional,
  sessionSummary,
  prefill,
  isPreview = false,
}: {
  eventId: number;
  token?: string;
  language?: string;
  isProfessional: boolean;
  sessionSummary: string;
  prefill?: WorkshopRegistrationPrefill;
  /** Admin preview: render the form, do not submit a registration. */
  isPreview?: boolean;
}) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const [registered, setRegistered] = useState(false);
  const formStarted = useRef(false);
  const form = useForm<RegistrationFormValues>({
    resolver: zodResolver(
      isProfessional
        ? RegistrationFormSchema.extend({ phone: z.string().trim().max(32) })
        : RegistrationFormSchema,
    ),
    defaultValues: {
      firstName: prefill?.firstName ?? "",
      lastName: prefill?.lastName ?? "",
      email: prefill?.email ?? "",
      phone: prefill?.phone ?? "",
      state: prefill?.state,
      schoolId: prefill?.schoolId,
      schoolName: prefill?.schoolName ?? "",
      role: prefill?.role,
      heardAboutWorkshop: "",
    },
  });

  const state = useWatch({ control: form.control, name: "state" });
  const register = useMutation(
    trpc.workshopEvents.register.mutationOptions({
      onSuccess: () => {
        if (language) {
          trackWorkshopRegistrationSubmitted(eventId, { language });
        }
        setRegistered(true);
        void queryClient.invalidateQueries(
          trpc.workshopEvents.listForCounselor.queryFilter(),
        );
      },
      onError: (error) => {
        if (language) {
          trackWorkshopRegistrationFailed(eventId, {
            language,
            reason: error.message || "unknown",
          });
        }
        form.setError("root", {
          message: error.message || "Something went wrong. Please try again.",
        });
      },
    }),
  );

  if (registered) {
    return (
      <div className={`${FRONT_DOOR_CARD_CLASS} min-w-0 text-center`}>
        <span className="mx-auto flex h-9 w-9 items-center justify-center rounded-full bg-success-subtle text-success">
          <Check className="h-5 w-5" aria-hidden />
        </span>
        <h2 className="mt-3 font-martina text-2xl font-normal tracking-[-0.02em] text-foreground">
          You&apos;re registered
        </h2>
        <p className="mt-2 text-[14.5px] leading-[1.55] text-muted-foreground">
          We&apos;ll email you the Zoom link soon. We&apos;ll also send a
          reminder the day before and the recording afterward.
        </p>
        <Button
          asChild
          variant="custom"
          className={`mt-5 ${FRONT_DOOR_PRIMARY_BUTTON_CLASS}`}
        >
          <a href={workshopCalendarPath(eventId, token)}>Add to calendar</a>
        </Button>
      </div>
    );
  }

  return (
    <>
      <div className={`${FRONT_DOOR_CARD_CLASS} min-w-0`}>
        <Form {...form}>
          <form
            id={FORM_ID}
            className={FRONT_DOOR_FIELD_STYLES}
            noValidate
            onFocusCapture={() => {
              if (formStarted.current || isPreview || !language) {
                return;
              }
              formStarted.current = true;
              trackWorkshopRegistrationFormStarted(eventId, { language });
            }}
            onSubmit={form.handleSubmit(
              (values) => {
                if (isPreview) {
                  return;
                }
                const usesSchoolDirectory = isMarbleServicedState(values.state);
                register.mutate({
                  eventId,
                  token,
                  firstName: values.firstName,
                  lastName: values.lastName,
                  email: values.email,
                  phone: values.phone,
                  state: values.state,
                  schoolId: usesSchoolDirectory ? values.schoolId : undefined,
                  schoolName: usesSchoolDirectory
                    ? undefined
                    : values.schoolName?.trim() || undefined,
                  role: values.role,
                  heardAboutWorkshop: values.heardAboutWorkshop,
                });
              },
              (errors) => {
                if (isPreview) {
                  return;
                }
                trackWorkshopValidationErrorShown(eventId, {
                  fields: Object.keys(errors),
                });
              },
            )}
          >
            <h2 className="font-martina text-2xl font-normal tracking-[-0.02em] text-foreground">
              Register
            </h2>
            <p className="mt-1 text-[13.5px] text-muted-foreground">
              Free to attend. We&apos;ll email you the Zoom link.
            </p>
            {form.formState.errors.root && (
              <FrontDoorNotice tone="error" className="mt-[18px]">
                {form.formState.errors.root.message}
              </FrontDoorNotice>
            )}
            <div className="grid min-w-0 gap-[18px] sm:grid-cols-2">
              <FormField
                control={form.control}
                name="firstName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>First name</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        autoComplete="given-name"
                        placeholder="Jane"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="lastName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Last name</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        autoComplete="family-name"
                        placeholder="Doe"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem className="sm:col-span-2">
                    <FormLabel>
                      {isProfessional ? "Work email" : "Email"}
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="email"
                        autoComplete="email"
                        placeholder={
                          isProfessional
                            ? "jane@school.org"
                            : "jane@example.com"
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {isProfessional ? null : (
                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem className="sm:col-span-2">
                      <FormLabel>Phone</FormLabel>
                      <FormControl>
                        <PhoneInput
                          value={field.value || undefined}
                          onChange={(value) => field.onChange(value ?? "")}
                          placeholder="(555) 000-0000"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
            </div>
            <FormField
              control={form.control}
              name="state"
              render={({ field }) => (
                <FormItem className="mt-[18px]">
                  <FormLabel>State</FormLabel>
                  <StateDropdown
                    value={field.value}
                    onValueChange={(next) => {
                      const current = form.getValues("state");
                      field.onChange(next);
                      if (current && current !== next) {
                        form.setValue("schoolId", undefined);
                        form.setValue("schoolName", "");
                      }
                    }}
                    placeholder="Select state"
                    menuTheme="lavender"
                  />
                  <FormMessage />
                </FormItem>
              )}
            />
            {isMarbleServicedState(state) || !state ? (
              <FormField
                control={form.control}
                name="schoolId"
                render={({ field }) => (
                  <FormItem className="mt-[18px]">
                    <FormLabel>
                      {isProfessional ? "School" : "Your child's school"}{" "}
                      <OptionalMark />
                    </FormLabel>
                    <SchoolSelect
                      state={state}
                      value={field.value}
                      onChange={field.onChange}
                      selectedLabel={prefill?.schoolName}
                    />
                    <FormMessage />
                  </FormItem>
                )}
              />
            ) : (
              <FormField
                control={form.control}
                name="schoolName"
                render={({ field }) => (
                  <FormItem className="mt-[18px]">
                    <FormLabel>
                      {isProfessional ? "School" : "Your child's school"}{" "}
                      <OptionalMark />
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        value={field.value ?? ""}
                        placeholder="School name"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
            <FormField
              control={form.control}
              name="role"
              render={({ field }) => (
                <FormItem className="mt-[18px]">
                  <FormLabel>Role or title</FormLabel>
                  <RoleDropdown
                    value={field.value}
                    onValueChange={field.onChange}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="heardAboutWorkshop"
              render={({ field }) => (
                <FormItem className="mt-[18px]">
                  <FormLabel>Where did you hear about the workshop?</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="e.g. a colleague, email, social media"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button
              type="submit"
              variant="custom"
              data-dd-action-name="workshops:register_button:submitted"
              className={`mt-5 ${FRONT_DOOR_PRIMARY_BUTTON_CLASS}`}
              loading={register.isPending}
            >
              Register
            </Button>
          </form>
        </Form>
      </div>
      <div className="fixed inset-x-0 bottom-0 z-20 border-t border-primary-hairline bg-background/95 px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] backdrop-blur md:hidden">
        <div className="mx-auto flex max-w-lg items-center justify-between gap-3">
          <p className="min-w-0 truncate text-[13px] leading-snug text-foreground-soft">
            {sessionSummary}
          </p>
          <Button
            type="submit"
            form={FORM_ID}
            variant="custom"
            data-dd-action-name="workshops:register_button:submitted"
            className="h-10 shrink-0 rounded-full border border-primary-soft-border bg-primary-soft px-4 text-[14px] font-medium text-foreground hover:bg-primary-soft-hover xs:px-5"
            loading={register.isPending}
          >
            Register
          </Button>
        </div>
      </div>
    </>
  );
}
